/**
 * SAIR DO EDITOR COM ALTERAÇÃO NÃO SALVA — `T2.44`, `T8.3`.
 *
 * ── O QUE FOI MEDIDO EM 27/09, E ELE REPRODUZ ───────────────────────────────────────────────
 *
 * Na vitrine, conferindo a **pré-condição antes do efeito** (que é o que três medições anteriores não
 * fizeram):
 *
 *     antes de alterar    1 forma no palco, botão "Desfazer" travado
 *     depois de inserir   2 formas, "Desfazer" **destravado** → alteração REGISTRADA
 *     clicar "Meus quadrinhos" no menu   **não perguntou nada**, e saiu do editor
 *
 * E a letra dos dois pontos é exatamente isso:
 *
 *     T2.44  *"quando aperta voltar, eu perco tudo"*
 *     T8.3   *"Adicionei um balão a um quadrinho já salvo e cliquei em 'Meus quadrinhos' NO MENU. Não
 *            houve nenhuma pergunta do tipo 'Sair sem salvar?'"*
 *
 * ── A CAUSA: A GUARDA EXISTIA NUMA SAÍDA, E HÁ TRÊS ─────────────────────────────────────────
 *
 * O `editar-quadrinho.tsx` pergunta quando o **editor** pede para voltar à lista — o caminho de
 * `setCurrentComic(null)`. Mas sair tem três portas, e só uma passava por ali:
 *
 *   1. o botão de voltar **do editor**        → perguntava
 *   2. qualquer `<Link>` do menu              → **não perguntava** (é o caso dele)
 *   3. fechar a aba ou recarregar             → o `beforeunload` do salvamento automático
 *
 * ── POR QUE UM REGISTRO, E NÃO UM `useContext` ──────────────────────────────────────────────
 *
 * O menu é montado **acima** do editor na árvore — ele aparece em toda rota, e o editor é uma delas.
 * Um contexto teria de nascer acima dos dois, o que significa envolver a aplicação inteira num
 * provedor para um estado que existe em uma tela só.
 *
 * Este arquivo é um registro de módulo: quem tem trabalho a perder **se anuncia** enquanto está
 * montado, e quem navega **pergunta** antes de sair. Duas linhas em cada ponta, e nada no meio.
 *
 * ── E POR QUE `window.confirm` CONTINUA SENDO A RESPOSTA AQUI ───────────────────────────────
 *
 * É a mesma razão já escrita no `editar-quadrinho.tsx`, e é a única exceção que o `T8.31` admite: a
 * versão do React Router aqui **não tem bloqueio de navegação**, então a decisão tem de ser
 * **síncrona**. Um modal do produto obrigaria a interromper a navegação, guardar o destino, esperar a
 * resposta e renavegar — quatro caminhos para alguém esquecer um, e o esquecido perde o trabalho da
 * criança, que é o defeito que estamos consertando.
 */

/** O que uma tela registra: uma pergunta que devolve `true` quando pode sair. */
type Guarda = () => boolean;

let guarda: Guarda | null = null;

/**
 * A tela que tem trabalho a perder se anuncia.
 *
 * Devolve a função de desfazer o registro — e ela é obrigatória de chamar na saída do efeito: uma
 * guarda que sobrevive à tela que a criou faz o menu perguntar sobre um editor que já não existe.
 */
export function registrarGuardaDeSaida(nova: Guarda): () => void {
  guarda = nova;
  return () => {
    // Só limpa se ainda for a MESMA: se outra tela registrou no meio, apagar aqui derrubaria a dela.
    if (guarda === nova) guarda = null;
  };
}

/**
 * Quem navega pergunta antes.
 *
 * Sem guarda registrada devolve `true` sem incomodar ninguém — é o caso de todas as telas menos uma.
 */
export function podeSair(): boolean {
  return guarda ? guarda() : true;
}

/** A frase, num lugar só: ela aparece nas três portas de saída e não pode divergir entre elas. */
export const AVISO_DE_SAIDA =
  "Você mexeu neste quadrinho e ainda não salvou.\n\n" +
  "Saindo agora, as mudanças desde o último salvamento se perdem. " +
  "Cancele e toque em Salvar se quiser guardar.";
